import { useParams, useNavigate } from 'react-router-dom';
import { useJobs } from '@/hooks/useJobs';
import { JobDetail } from '@/components/jobs/JobDetail';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOJobMeta } from '@/components/SEOJobMeta';
import { SEOJobStructuredData } from '@/components/SEOJobStructuredData';
import { SEOBreadcrumbSchema } from '@/components/SEOBreadcrumbSchema';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator 
} from '@/components/ui/breadcrumb';

const JobPage = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { allJobs, isLoading, error } = useJobs();

  // Find the job by ID
  const job = allJobs.find(j => j.id === jobId);

  const handleBack = () => {
    navigate('/bewerbende', { state: { scrollToFilters: true } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8">
          <div className="container mx-auto px-4">
            <div className="space-y-6">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-96 w-full" />
              <div className="space-y-4">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8">
          <div className="container mx-auto px-4">
            <Alert variant="destructive" className="max-w-md mx-auto">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Fehler beim Laden der Stellenangebote: {error}
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="py-8">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-md mx-auto space-y-6">
              <h1 className="text-4xl font-display font-extralight bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent">404</h1>
              <h2 className="text-xl font-display font-extralight text-muted-foreground">Stellenanzeige nicht gefunden</h2>
              <p className="text-muted-foreground">
                Die gesuchte Stellenanzeige existiert nicht oder wurde bereits entfernt.
              </p>
              <Button onClick={handleBack} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Zurück zu den Stellenangeboten
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOJobMeta job={job} />
      <SEOJobStructuredData job={job} />
      <SEOBreadcrumbSchema items={[
        { name: "Home", path: "/" },
        { name: "Stellenangebote", path: "/bewerbende" },
        { name: job.title, path: `/stellenangebote/${job.id}` }
      ]} />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        
        <main className="py-8 pt-24">
          {/* Breadcrumb Navigation */}
          <div className="container mx-auto px-4 mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">Startseite</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="/bewerbende">Stellenangebote</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{job.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <JobDetail job={job} onBack={handleBack} />
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default JobPage;